import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import targetsApi from "../services/targetsApi";
import translations from "../assets/locals/translations";

import "../styles/Targets.css";
import DataGridTable from "./table/DataGridTable";

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons/faEllipsisV";

function toMonthLabel(currentLanguage, month) {
  // month is 1-based
  return new Date(2000, month - 1, 1).toLocaleString(currentLanguage || "en", { month: "long" });
}

function parsePositiveInt(raw) {
  const v = parseInt(String(raw ?? "").replace(/[^0-9]/g, ""), 10);
  return Number.isNaN(v) ? 0 : v;
}

function todayIsoDate() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function Targets() {
  const currentLanguage = useSelector((state) => state.language.language);
  const t = translations[currentLanguage] || translations["en"];

  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);

  const isPastMonth = year < currentYear || (year === currentYear && month < currentMonth);
  const canEdit = !isPastMonth;

  const yearOptions = useMemo(() => {
    const start = currentYear - 5;
    const end = currentYear + 5;
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [currentYear]);

  // Inside dialogs we control Single vs Bulk (and Attendance for activities)
  const [categoryAddMode, setCategoryAddMode] = useState("single"); // "single" | "bulk"
  const [activityAddMode, setActivityAddMode] = useState("single"); // "single" | "bulk" | "attendance"

  // Period/person selection inside modals
  const [modalUserId, setModalUserId] = useState(null); // null => Me
  const [modalYear, setModalYear] = useState(currentYear);
  const [modalMonth, setModalMonth] = useState(currentMonth);

  const modalIsPastMonth =
    modalYear < currentYear || (modalYear === currentYear && modalMonth < currentMonth);

  const [employeesForTargets, setEmployeesForTargets] = useState([]);
  // Grid person filter (All / Me / Employee)
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("all"); // "all" | "me" | <userId>
  const canManageOthersTargets = employeesForTargets.length > 0;

  const viewMode = selectedEmployeeId === "all" ? "all" : "me";

  const [reportEmployees, setReportEmployees] = useState([]); // for all-person view (monthly report)
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportError, setReportError] = useState("");

  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const [loadingSummary, setLoadingSummary] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState({ categories: [], activities: [] });

  const [categoryDrafts, setCategoryDrafts] = useState({});
  const [activityDrafts, setActivityDrafts] = useState({});

  useEffect(() => {
    setCategoryDrafts(
      Object.fromEntries(
        (summary.categories || []).map((c) => [String(c.id), String(c.monthlyTarget ?? 0)]),
      ),
    );
    setActivityDrafts(
      Object.fromEntries(
        (summary.activities || []).map((a) => [String(a.id), String(a.monthlyTarget ?? 0)]),
      ),
    );
  }, [summary]);

  const loadEmployees = async () => {
    setLoadingEmployees(true);
    setError("");
    try {
      const res = await targetsApi.getEmployeesForTargets();
      if (res?.isSuccess && Array.isArray(res?.result)) setEmployeesForTargets(res.result);
      else setEmployeesForTargets([]);
    } catch (e) {
      setEmployeesForTargets([]);
      setError(e?.response?.data?.result || e?.message || "Failed to load employees");
    } finally {
      setLoadingEmployees(false);
    }
  };

  const loadSummary = async (opts = {}) => {
    const targetUserId =
      opts.userId !== undefined
        ? opts.userId
        : selectedEmployeeId === "me"
          ? null
          : selectedEmployeeId === "all"
            ? null
            : parsePositiveInt(selectedEmployeeId);
    const targetYear = opts.year !== undefined ? opts.year : year;
    const targetMonth = opts.month !== undefined ? opts.month : month;

    setLoadingSummary(true);
    setError("");
    try {
      const res =
        targetUserId != null
          ? await targetsApi.getEmployeeSummary(targetUserId, targetYear, targetMonth)
          : await targetsApi.getMeSummary(targetYear, targetMonth);

      if (res?.isSuccess && res?.result) {
        setSummary({
          categories: res.result.categories || [],
          activities: res.result.activities || [],
        });
      } else {
        setSummary({ categories: [], activities: [] });
      }
    } catch (e) {
      setSummary({ categories: [], activities: [] });
      setError(e?.response?.data?.result || e?.message || "Failed to load targets");
    } finally {
      setLoadingSummary(false);
    }
  };

  useEffect(() => {
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep default person filter as "All" even if user cannot
  // manage others, so the All Targets view is shown by default.

  useEffect(() => {
    if (viewMode !== "me") return;
    const userId =
      selectedEmployeeId === "me" ? null : parsePositiveInt(selectedEmployeeId);
    loadSummary({ userId, year, month });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, selectedEmployeeId, viewMode]);

  useEffect(() => {
    if (viewMode !== "all") return;
    setLoadingReport(true);
    setReportError("");
    setReportEmployees([]);

    targetsApi
      .getReport({ period: "month", year, month })
      .then((res) => {
        if (res?.isSuccess) {
          const rows = Array.isArray(res?.result) ? res.result : [];
          // Only keep employees who actually have target entries (details rows)
          const filtered = rows.filter((emp) => Array.isArray(emp.details) && emp.details.length > 0);
          setReportEmployees(filtered);
        }
        else setReportError(res?.result || "Failed to load target report");
      })
      .catch((e) => {
        setReportError(e?.response?.data?.result || e?.message || "Failed to load target report");
      })
      .finally(() => setLoadingReport(false));
  }, [viewMode, year, month]);

  const saveCategoryTarget = async (cat) => {
    if (!canEdit) return;
    const monthlyTarget = parsePositiveInt(categoryDrafts[String(cat.id)]);
    const payload = {
      targetType: "category",
      enquiryCategoryId: cat.id,
      year,
      month,
      monthlyTarget,
    };
    if (selectedEmployeeId != null) payload.userId = selectedEmployeeId;

    const res = await targetsApi.setMonthlyTarget(payload);
    if (res?.isSuccess) await loadSummary();
    else setError(res?.result || "Failed to save category target");
  };

  const deleteCategoryTarget = async (cat) => {
    if (!canEdit) return;
    const params = {
      targetType: "category",
      enquiryCategoryId: cat.id,
      year,
      month,
    };
    if (selectedEmployeeId != null) params.userId = selectedEmployeeId;

    const res = await targetsApi.deleteMonthlyTarget(params);
    if (res?.isSuccess) await loadSummary();
    else setError(res?.result || "Failed to delete category target");
  };

  const saveActivityTarget = async (act) => {
    if (!canEdit) return;
    const monthlyTarget = parsePositiveInt(activityDrafts[String(act.id)]);
    const payload = {
      targetType: "activity",
      activityId: act.id,
      year,
      month,
      monthlyTarget,
    };
    if (selectedEmployeeId != null) payload.userId = selectedEmployeeId;

    const res = await targetsApi.setMonthlyTarget(payload);
    if (res?.isSuccess) await loadSummary();
    else setError(res?.result || "Failed to save activity target");
  };

  const deleteActivityTarget = async (act) => {
    if (!canEdit) return;
    const params = {
      targetType: "activity",
      activityId: act.id,
      year,
      month,
    };
    if (selectedEmployeeId != null) params.userId = selectedEmployeeId;

    const res = await targetsApi.deleteMonthlyTarget(params);
    if (res?.isSuccess) await loadSummary();
    else setError(res?.result || "Failed to delete activity target");
  };

  // -------------------------
  // Bulk Category Targets modal
  // -------------------------
  const [openBulkCategoryModal, setOpenBulkCategoryModal] = useState(false);
  const [bulkCategoryRows, setBulkCategoryRows] = useState([]); // [{id, name, value}]
  const [loadingBulkCategories, setLoadingBulkCategories] = useState(false);
  const [savingBulkCategories, setSavingBulkCategories] = useState(false);

  const openBulkCategoryModalInternal = async () => {
    if (modalIsPastMonth) {
      setError("Not allowed for past months.");
      return;
    }
    setError("");
    setOpenBulkCategoryModal(true);
    setBulkCategoryRows([]);

    setLoadingBulkCategories(true);
    try {
      const res = await targetsApi.getEnquiryCategories();
      const cats = res?.isSuccess && Array.isArray(res?.result) ? res.result : [];
      const filtered = cats.filter((c) => !(c.name === "None" || c.id === 1));
      setBulkCategoryRows(
        filtered.map((c) => ({
          id: c.id,
          name: c.name,
          value: "",
        })),
      );
    } catch (e) {
      setError(e?.response?.data?.result || e?.message || "Failed to load categories");
      setOpenBulkCategoryModal(false);
    } finally {
      setLoadingBulkCategories(false);
    }
  };

  const saveBulkCategoryTargets = async () => {
    if (modalIsPastMonth) return;
    const tasks = [];
    bulkCategoryRows.forEach((r) => {
      const v = parsePositiveInt(r.value);
      if (v > 0) {
        const payload = {
          targetType: "category",
          enquiryCategoryId: r.id,
          year: modalYear,
          month: modalMonth,
          monthlyTarget: v,
          addToExisting: true,
        };
        if (modalUserId != null) payload.userId = modalUserId;
        tasks.push(targetsApi.setMonthlyTarget(payload));
      }
    });

    if (!tasks.length) {
      setError("Nothing to save. Enter at least one target.");
      return;
    }

    setSavingBulkCategories(true);
    setError("");
    try {
      await Promise.all(tasks);
      setOpenBulkCategoryModal(false);
      await refreshCurrentView();
    } catch (e) {
      setError(e?.response?.data?.result || e?.message || "Failed to save category targets");
    } finally {
      setSavingBulkCategories(false);
    }
  };

  // -------------------------
  // Bulk Activity Targets modal
  // -------------------------
  const [openBulkActivityModal, setOpenBulkActivityModal] = useState(false);
  const [bulkActivityRows, setBulkActivityRows] = useState([]); // [{id, name, value}]
  const [loadingBulkActivities, setLoadingBulkActivities] = useState(false);
  const [savingBulkActivities, setSavingBulkActivities] = useState(false);

  const openBulkActivityModalInternal = async () => {
    if (modalIsPastMonth) {
      setError("Not allowed for past months.");
      return;
    }
    setError("");
    setOpenBulkActivityModal(true);
    setBulkActivityRows([]);

    setLoadingBulkActivities(true);
    try {
      const res = await targetsApi.getActivities();
      const acts = res?.isSuccess && Array.isArray(res?.result) ? res.result : [];
      setBulkActivityRows(
        acts.map((a) => ({
          id: a.id,
          name: a.name,
          value: "",
        })),
      );
    } catch (e) {
      setError(e?.response?.data?.result || e?.message || "Failed to load activities");
      setOpenBulkActivityModal(false);
    } finally {
      setLoadingBulkActivities(false);
    }
  };

  const saveBulkActivityTargets = async () => {
    if (modalIsPastMonth) return;
    const tasks = [];
    bulkActivityRows.forEach((r) => {
      const v = parsePositiveInt(r.value);
      if (v > 0) {
        const payload = {
          targetType: "activity",
          activityId: r.id,
          year: modalYear,
          month: modalMonth,
          monthlyTarget: v,
          addToExisting: true,
        };
        if (modalUserId != null) payload.userId = modalUserId;
        tasks.push(targetsApi.setMonthlyTarget(payload));
      }
    });

    if (!tasks.length) {
      setError("Nothing to save. Enter at least one target.");
      return;
    }

    setSavingBulkActivities(true);
    setError("");
    try {
      await Promise.all(tasks);
      setOpenBulkActivityModal(false);
      await refreshCurrentView();
    } catch (e) {
      setError(e?.response?.data?.result || e?.message || "Failed to save activity targets");
    } finally {
      setSavingBulkActivities(false);
    }
  };

  // -------------------------
  // Mark attendance modal
  // -------------------------
  const [openAttendanceDialog, setOpenAttendanceDialog] = useState(false);
  const [attendanceActivities, setAttendanceActivities] = useState([]);
  const [loadingAttendanceActivities, setLoadingAttendanceActivities] = useState(false);
  const [attendanceActivityId, setAttendanceActivityId] = useState("");
  const [attendanceDate, setAttendanceDate] = useState(todayIsoDate());
  const [attendanceAttended, setAttendanceAttended] = useState(true);
  const [savingAttendance, setSavingAttendance] = useState(false);
  const [attendanceAllowedActivityIds, setAttendanceAllowedActivityIds] = useState(new Set());
  const [attendanceCheckLoading, setAttendanceCheckLoading] = useState(false);
  const [attendanceCheckLoaded, setAttendanceCheckLoaded] = useState(false);

  const openAttendanceDialogInternal = async () => {
    setError("");
    setOpenAttendanceDialog(true);
    setAttendanceDate(todayIsoDate());
    setAttendanceAttended(true);
    setAttendanceAllowedActivityIds(new Set());
    setAttendanceCheckLoaded(false);

    // Prefill attendance period/person from current modal selections
    const prefUser = resolveUserId(selectedEmployeeId);
    setModalUserId(prefUser);
    setModalYear(year);
    setModalMonth(month);

    if (!attendanceActivities.length) {
      setLoadingAttendanceActivities(true);
      try {
        const res = await targetsApi.getActivities();
        const acts = res?.isSuccess && Array.isArray(res?.result) ? res.result : [];
        setAttendanceActivities(acts);
        setAttendanceActivityId(acts?.[0]?.id != null ? String(acts[0].id) : "");
      } catch (e) {
        setError(e?.response?.data?.result || e?.message || "Failed to load activities");
        setOpenAttendanceDialog(false);
      } finally {
        setLoadingAttendanceActivities(false);
      }
    }
    // If activities were already loaded, keep the previously selected activity.
    if (!attendanceActivityId && attendanceActivities?.[0]?.id != null) {
      setAttendanceActivityId(String(attendanceActivities[0].id));
    }

    // Load allowed activities based on current selections
    await loadAttendanceAllowedActivities(prefUser, year, month);
  };

  const loadAttendanceAllowedActivities = async (userIdParam, yParam, mParam) => {
    setAttendanceCheckLoading(true);
    setAttendanceCheckLoaded(false);
    try {
      const res =
        userIdParam == null
          ? await targetsApi.getMeSummary(yParam, mParam)
          : await targetsApi.getEmployeeSummary(userIdParam, yParam, mParam);
      const acts = res?.isSuccess ? (res?.result?.activities || []) : [];
      const idSet = new Set((acts || []).map((a) => a.id).filter(Boolean));
      setAttendanceAllowedActivityIds(idSet);
    } catch (e) {
      setAttendanceAllowedActivityIds(new Set());
    } finally {
      setAttendanceCheckLoaded(true);
      setAttendanceCheckLoading(false);
    }
  };

  const saveAttendance = async () => {
    const actId = parsePositiveInt(attendanceActivityId);
    if (!actId) {
      setError("Select an activity.");
      return;
    }
    if (attendanceCheckLoaded && !attendanceAllowedActivityIds.has(actId)) {
      setError("You cannot add attendance because selected person does not have this activity target for this month.");
      return;
    }
    if (!attendanceDate) {
      setError("Pick a date.");
      return;
    }

    setSavingAttendance(true);
    setError("");
    try {
      const res = await targetsApi.logActivity({
        activityId: actId,
        date: attendanceDate,
        attended: true,
        userId: modalUserId != null ? modalUserId : undefined,
      });
      if (!res?.isSuccess) {
        setError(res?.result || "Failed to save attendance");
        return;
      }
      setOpenAttendanceDialog(false);
      // Attendance logging is for "Me" only, but reload for the selected modal period.
      await refreshCurrentView();
    } catch (e) {
      setError(e?.response?.data?.result || e?.message || "Failed to save attendance");
    } finally {
      setSavingAttendance(false);
    }
  };

  // -------------------------
  // Add Category Target modal
  // -------------------------
  const [openAddCategory, setOpenAddCategory] = useState(false);
  const [enquiryCategories, setEnquiryCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [newCategoryTarget, setNewCategoryTarget] = useState("");

  const openAddCategoryModal = async () => {
    setError("");
    setModalUserId(resolveUserId(selectedEmployeeId));
    setModalYear(year);
    setModalMonth(month);
    setCategoryAddMode("single");
    setOpenAddCategory(true);
    setNewCategoryTarget("");
    let firstId = null;

    if (!enquiryCategories.length) {
      setLoadingCategories(true);
      try {
        const res = await targetsApi.getEnquiryCategories();
        if (res?.isSuccess && Array.isArray(res?.result)) {
          setEnquiryCategories(res.result);
          firstId = res.result?.[0]?.id ?? null;
        }
      } catch (e) {
        setError(e?.response?.data?.result || e?.message || "Failed to load categories");
      } finally {
        setLoadingCategories(false);
      }
    }
    if (firstId == null) firstId = enquiryCategories?.[0]?.id ?? null;
    setSelectedCategoryId(firstId != null ? String(firstId) : "");
  };

  const saveNewCategoryTarget = async () => {
    if (modalIsPastMonth) return;
    const catId = parsePositiveInt(selectedCategoryId);
    const monthlyTarget = parsePositiveInt(newCategoryTarget);
    if (!catId) {
      setError("Select a category.");
      return;
    }
    if (monthlyTarget <= 0) {
      setError("Enter a valid target number.");
      return;
    }

    const payload = {
      targetType: "category",
      enquiryCategoryId: catId,
      year: modalYear,
      month: modalMonth,
      monthlyTarget,
      // When adding a category target from this dialog, always add on top
      // of any existing target for the same period (sum behaviour).
      addToExisting: true,
    };
    if (modalUserId != null) payload.userId = modalUserId;

    const res = await targetsApi.setMonthlyTarget(payload);
    if (res?.isSuccess) {
      setOpenAddCategory(false);
      await refreshCurrentView();
    } else setError(res?.result || "Failed to add category target");
  };

  // -------------------------
  // Add Activity Target modal
  // -------------------------
  const [openAddActivity, setOpenAddActivity] = useState(false);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [selectedActivityId, setSelectedActivityId] = useState("");
  const [newActivityTarget, setNewActivityTarget] = useState("");

  // Optional: create new activity
  const [newActivityName, setNewActivityName] = useState("");
  const [newActivityDesc, setNewActivityDesc] = useState("");

  const openAddActivityModal = async () => {
    setError("");
    setModalUserId(resolveUserId(selectedEmployeeId));
    setModalYear(year);
    setModalMonth(month);
    setActivityAddMode("single");
    setOpenAddActivity(true);
    setNewActivityTarget("");
    setNewActivityName("");
    setNewActivityDesc("");
    let firstId = null;

    if (!activities.length) {
      setLoadingActivities(true);
      try {
        const res = await targetsApi.getActivities();
        if (res?.isSuccess && Array.isArray(res?.result)) {
          setActivities(res.result);
          firstId = res.result?.[0]?.id ?? null;
        }
      } catch (e) {
        setError(e?.response?.data?.result || e?.message || "Failed to load activities");
      } finally {
        setLoadingActivities(false);
      }
    }
    if (firstId == null) firstId = activities?.[0]?.id ?? null;
    setSelectedActivityId(firstId != null ? String(firstId) : "");
  };

  const saveNewActivityTarget = async () => {
    if (modalIsPastMonth) return;
    const monthlyTarget = parsePositiveInt(newActivityTarget);
    if (monthlyTarget <= 0) {
      setError("Enter a valid target number.");
      return;
    }

    const trimmedNewName = (newActivityName || "").trim();
    if (trimmedNewName) {
      const createRes = await targetsApi.createActivity({
        name: trimmedNewName,
        description: (newActivityDesc || "").trim() || undefined,
      });
      if (!createRes?.isSuccess || !createRes?.result?.id) {
        setError(createRes?.result || "Failed to create activity");
        return;
      }

      const payload = {
        targetType: "activity",
        activityId: createRes.result.id,
        year: modalYear,
        month: modalMonth,
        monthlyTarget,
        // New activity starts fresh; no need to sum with existing.
      };
      if (modalUserId != null) payload.userId = modalUserId;

      const res = await targetsApi.setMonthlyTarget(payload);
      if (res?.isSuccess) {
        setOpenAddActivity(false);
        await refreshCurrentView();
      } else setError(res?.result || "Failed to add activity target");
      return;
    }

    const actId = parsePositiveInt(selectedActivityId);
    if (!actId) {
      setError("Select an activity.");
      return;
    }

    const payload = {
      targetType: "activity",
      activityId: actId,
      year: modalYear,
      month: modalMonth,
      monthlyTarget,
      // When adding an activity target from this dialog for an
      // already-targeted activity, add on top of the existing target.
      addToExisting: true,
    };
    if (modalUserId != null) payload.userId = modalUserId;

    const res = await targetsApi.setMonthlyTarget(payload);
    if (res?.isSuccess) {
      setOpenAddActivity(false);
      await refreshCurrentView();
    } else setError(res?.result || "Failed to add activity target");
  };

  // -------------------------
  // Log Activity dialog
  // -------------------------
  const [openLogActivity, setOpenLogActivity] = useState(false);
  const [activityForLog, setActivityForLog] = useState(null);
  const [logDate, setLogDate] = useState(todayIsoDate());
  const [logAttended, setLogAttended] = useState(true);
  const [savingLog, setSavingLog] = useState(false);

  const openLogActivityDialog = (act) => {
    setError("");
    setActivityForLog(act);
    setLogDate(todayIsoDate());
    setLogAttended(true);
    setOpenLogActivity(true);
  };

  const saveLogActivity = async () => {
    if (!activityForLog) return;
    if (!logDate) {
      setError("Pick a date.");
      return;
    }
    setSavingLog(true);
    try {
      const res = await targetsApi.logActivity({
        activityId: activityForLog.id,
        date: logDate,
        attended: !!logAttended,
      });
      if (res?.isSuccess) {
        setOpenLogActivity(false);
        setActivityForLog(null);
        await loadSummary();
      } else setError(res?.result || "Failed to log activity");
    } catch (e) {
      setError(e?.response?.data?.result || e?.message || "Failed to log activity");
    } finally {
      setSavingLog(false);
    }
  };

  const resolveUserId = (v) => {
    if (v == null) return null;
    if (v === "me" || v === "all") return null;
    const id = parsePositiveInt(v);
    return id || null;
  };

  const currentUserIdForActions = resolveUserId(selectedEmployeeId);

  const refreshCurrentView = async () => {
    if (viewMode === "all") {
      setReportError("");
      setLoadingReport(true);
      try {
        const rep = await targetsApi.getReport({ period: "month", year, month });
        if (rep?.isSuccess && Array.isArray(rep?.result)) setReportEmployees(rep.result);
        else setReportError(rep?.result || "Failed to load report");
      } catch (e) {
        setReportError(e?.response?.data?.result || e?.message || "Failed to load report");
      } finally {
        setLoadingReport(false);
      }
      return;
    }
    await loadSummary({ userId: currentUserIdForActions, year, month });
  };

  // -------------------------
  // Edit target dialog (grid action)
  // -------------------------
  const [openEditTarget, setOpenEditTarget] = useState(false);
  const [editRow, setEditRow] = useState(null); // { type, name, refId, target }
  const [editTargetValue, setEditTargetValue] = useState("");
  const [savingEditTarget, setSavingEditTarget] = useState(false);

  const openEditTargetDialog = (row) => {
    setError("");
    setEditRow(row);
    setEditTargetValue(String(row?.target ?? ""));
    setOpenEditTarget(true);
  };

  const saveEditedTarget = async () => {
    if (!editRow) return;
    if (isPastMonth) {
      setError("Targets can be added or edited only for current or future months.");
      return;
    }
    const val = parsePositiveInt(editTargetValue);
    if (val <= 0) {
      setError("Enter a valid target number.");
      return;
    }

    setSavingEditTarget(true);
    try {
      const payload = {
        targetType: editRow.type,
        year,
        month,
        monthlyTarget: val,
      };
      if (editRow.type === "category") payload.enquiryCategoryId = editRow.refId;
      if (editRow.type === "activity") payload.activityId = editRow.refId;
      const userIdForPayload = editRow?.userId != null ? editRow.userId : currentUserIdForActions;
      if (userIdForPayload != null) payload.userId = userIdForPayload;

      const res = await targetsApi.setMonthlyTarget(payload);
      if (!res?.isSuccess) {
        setError(res?.result || "Failed to save target");
        return;
      }
      setOpenEditTarget(false);
      setEditRow(null);
      if (viewMode === "all") {
        const rep = await targetsApi.getReport({ period: "month", year, month });
        if (rep?.isSuccess && Array.isArray(rep?.result)) setReportEmployees(rep.result);
      } else {
        await loadSummary({ userId: currentUserIdForActions, year, month });
      }
    } catch (e) {
      setError(e?.response?.data?.result || e?.message || "Failed to save target");
    } finally {
      setSavingEditTarget(false);
    }
  };

  const deleteTargetFromGrid = async (row) => {
    if (!row) return;
    if (isPastMonth) {
      setError("Targets can be deleted only for current or future months.");
      return;
    }
    try {
      const params = { targetType: row.type, year, month };
      if (row.type === "category") params.enquiryCategoryId = row.refId;
      if (row.type === "activity") params.activityId = row.refId;
      const userIdForParams = row?.userId != null ? row.userId : currentUserIdForActions;
      if (userIdForParams != null) params.userId = userIdForParams;

      const res = await targetsApi.deleteMonthlyTarget(params);
      if (!res?.isSuccess) {
        setError(res?.result || "Failed to delete target");
        return;
      }

      // Optimistic UI update (so grid refreshes instantly)
      if (viewMode === "all") {
        setReportEmployees((prev) => {
          const list = Array.isArray(prev) ? prev : [];
          const next = list
            .map((emp) => {
              if (emp.userId !== row.userId) return emp;
              const details = Array.isArray(emp.details) ? emp.details : [];
              const filteredDetails = details.filter((d) => !(d.type === row.type && d.id === row.refId));
              return { ...emp, details: filteredDetails };
            })
            .filter((emp) => (Array.isArray(emp.details) ? emp.details.length > 0 : false));
          return next;
        });
      } else {
        setSummary((prev) => {
          const next = { ...(prev || {}) };
          if (row.type === "category") next.categories = (next.categories || []).filter((c) => c.id !== row.refId);
          if (row.type === "activity") next.activities = (next.activities || []).filter((a) => a.id !== row.refId);
          return next;
        });
      }

      if (viewMode === "all") {
        const rep = await targetsApi.getReport({ period: "month", year, month });
        if (rep?.isSuccess && Array.isArray(rep?.result)) setReportEmployees(rep.result);
      } else {
        await loadSummary({ userId: currentUserIdForActions, year, month });
      }
    } catch (e) {
      setError(e?.response?.data?.result || e?.message || "Failed to delete target");
    }
  };

  const allGridColumns = useMemo(() => {
    const columns = [
      { field: "employee_name", headerName: t.employeeName || "Employee", minWidth: 180, flex: 1 },
      { field: "name", headerName: t.name || "Name", minWidth: 180, flex: 1 },
      { field: "type", headerName: "Type", minWidth: 110, flex: 1 },
      { field: "target", headerName: "Target", minWidth: 90, flex: 1 },
      { field: "completed", headerName: "Completed", minWidth: 100, flex: 1 },
      { field: "remaining", headerName: "Remaining", minWidth: 100, flex: 1 },
      { field: "pct", headerName: "%", minWidth: 70, flex: 1 },
      {
        field: "actions",
        headerName: <FontAwesomeIcon icon={faEllipsisV} style={{ marginRight: "15px" }} />,
        headerAlign: "right",
        align: "right",
        minWidth: 120,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <div className="d-flex justify-content-center dotHoverempicon">
            <FontAwesomeIcon icon={faEllipsisV} />
            <div className="expandDiv">
              <button className="myActionBtn m-1" onClick={() => openEditTargetDialog(params.row)} disabled={!canEdit}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" className="bi bi-pencil-square" viewBox="0 0 16 16">
                  <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                  <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z" />
                </svg>
              </button>
              <button onClick={() => deleteTargetFromGrid(params.row)} className="myActionBtn m-1" disabled={!canEdit}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" className="bi bi-trash3" viewBox="0 0 16 16">
                  <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5ZM11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0H11Zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5h9.916Zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47ZM8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5Z" />
                </svg>
              </button>
            </div>
          </div>
        ),
      },
    ];
    return columns;
  }, [t.employeeName, t.name, canEdit]);

  const allGridRows = useMemo(() => {
    const rows = [];
    let indexID = 0;
    (reportEmployees || []).forEach((emp) => {
      const details = Array.isArray(emp.details) ? emp.details : [];
      details.forEach((d) => {
        const target = d.target ?? 0;
        const completed = d.completed ?? 0;
        const remaining = d.remaining ?? Math.max(0, target - completed);
        // Extra safety: show only real entries
        if (!(target > 0 || completed > 0 || remaining > 0)) return;
        const pct = target > 0 ? Math.min(100, Math.round((completed / target) * 100)) : 0;
        rows.push({
          indexID: ++indexID,
          userId: emp.userId,
          employee_name: emp.userName || `User ${emp.userId}`,
          refId: d.id,
          name: d.name || "-",
          type: d.type || "-",
          target,
          completed,
          remaining,
          pct: `${pct}%`,
        });
      });
    });
    return rows;
  }, [reportEmployees]);

  const personGridColumns = useMemo(() => {
    return [
      { field: "name", headerName: t.name || "Name", minWidth: 180, flex: 1 },
      { field: "type", headerName: "Type", minWidth: 110, flex: 1 },
      { field: "target", headerName: "Target", minWidth: 90, flex: 1 },
      { field: "completed", headerName: "Completed", minWidth: 100, flex: 1 },
      { field: "remaining", headerName: "Remaining", minWidth: 100, flex: 1 },
      { field: "pct", headerName: "%", minWidth: 70, flex: 1 },
      {
        field: "actions",
        headerName: <FontAwesomeIcon icon={faEllipsisV} style={{ marginRight: "15px" }} />,
        headerAlign: "right",
        align: "right",
        minWidth: 120,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <div className="d-flex justify-content-center dotHoverempicon">
            <FontAwesomeIcon icon={faEllipsisV} />
            <div className="expandDiv">
              <button className="myActionBtn m-1" onClick={() => openEditTargetDialog(params.row)} disabled={!canEdit}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" className="bi bi-pencil-square" viewBox="0 0 16 16">
                  <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                  <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z" />
                </svg>
              </button>
              <button onClick={() => deleteTargetFromGrid(params.row)} className="myActionBtn m-1" disabled={!canEdit}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" className="bi bi-trash3" viewBox="0 0 16 16">
                  <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5ZM11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H2.506a.58.58 0 0 0-.01 0H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1h-.995a.59.59 0 0 0-.01 0H11Zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5h9.916Zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47ZM8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5Z" />
                </svg>
              </button>
              {params.row.type === "activity" && selectedEmployeeId === "me" && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => openLogActivityDialog({ id: params.row.refId, name: params.row.name })}
                  sx={{ marginLeft: 1 }}
                >
                  Log
                </Button>
              )}
            </div>
          </div>
        ),
      },
    ];
  }, [t.name, canEdit, selectedEmployeeId]);

  const personGridRows = useMemo(() => {
    const rows = [];
    let indexID = 0;
    const addRow = (d, type) => {
      const target = d.monthlyTarget ?? d.target ?? 0;
      const completed = d.completed ?? 0;
      const remaining = d.remaining ?? Math.max(0, target - completed);
      if (!(target > 0 || completed > 0 || remaining > 0)) return;
      const pct = target > 0 ? Math.min(100, Math.round((completed / target) * 100)) : 0;
      rows.push({
        indexID: ++indexID,
        refId: d.id,
        name: d.name || "-",
        type,
        target,
        completed,
        remaining,
        pct: `${pct}%`,
      });
    };

    (summary.categories || []).forEach((c) => addRow(c, "category"));
    (summary.activities || []).forEach((a) => addRow(a, "activity"));
    return rows;
  }, [summary]);

  return (
    <div className="targets-container" style={{ fontFamily: "Poppins" }}>
      <div className="targets-header">
        <div>
          <Typography variant="h6" className="targets-title">
            {viewMode === "all" ? "All Targets" : "My Targets"}
          </Typography>
        </div>

        <div className="targets-filtersRow">
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="targets-person-filter-label">Person</InputLabel>
            <Select
              labelId="targets-person-filter-label"
              value={selectedEmployeeId}
              label="Person"
              onChange={(e) => setSelectedEmployeeId(e.target.value)}
              disabled={loadingEmployees}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="me">Me</MenuItem>
              {employeesForTargets.map((emp) => (
                <MenuItem key={emp.id ?? emp.user_id} value={String(emp.user_id ?? emp.id)}>
                  {emp.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel id="targets-year-filter-label">Year</InputLabel>
            <Select
              labelId="targets-year-filter-label"
              value={year}
              label="Year"
              onChange={(e) => setYear(parsePositiveInt(e.target.value))}
            >
              {yearOptions.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="targets-month-filter-label">Month</InputLabel>
            <Select
              labelId="targets-month-filter-label"
              value={month}
              label="Month"
              onChange={(e) => setMonth(parsePositiveInt(e.target.value))}
            >
              {Array.from({ length: 12 }, (_, idx) => idx + 1).map((m) => (
                <MenuItem key={m} value={m}>
                  {toMonthLabel(currentLanguage, m)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={openAddCategoryModal}
            disabled={!canEdit}
            sx={{ minWidth: 160, borderColor: "rgba(0,0,0,0.25)", color: "rgba(0,0,0,0.8)" }}
          >
            Add Category Target
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={openAddActivityModal}
            disabled={!canEdit}
            sx={{ minWidth: 160, borderColor: "rgba(0,0,0,0.25)", color: "rgba(0,0,0,0.8)" }}
          >
            Add Activity Target
          </Button>
        </div>
      </div>

      {viewMode === "me" && (error || loadingSummary) && (
        <div className="targets-status">
          {loadingSummary ? <CircularProgress size={22} /> : null}
          {error ? <Typography color="error" variant="body2">{error}</Typography> : null}
        </div>
      )}

      {/* action buttons are shown in the header filters row */}

      {viewMode === "all" && (reportError || loadingReport) && (
        <div className="targets-status">
          {loadingReport ? <CircularProgress size={22} /> : null}
          {reportError ? <Typography color="error" variant="body2">{reportError}</Typography> : null}
        </div>
      )}

      {viewMode === "me" && (
        <div className="targets-allGrid">
          <DataGridTable columns={personGridColumns} data={personGridRows} storageKey="targets_person_grid" />
        </div>
      )}

      <Dialog open={openEditTarget} onClose={() => setOpenEditTarget(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Target</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            {editRow?.type === "category" ? "Category" : "Activity"}: {editRow?.name || "-"}
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            Period: {toMonthLabel(currentLanguage, month)} {year}
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Monthly Target"
            type="number"
            value={editTargetValue}
            onChange={(e) => setEditTargetValue(e.target.value)}
            sx={{ marginTop: 1 }}
          />
          {isPastMonth && (
            <Typography color="error" variant="body2" sx={{ marginTop: 1 }}>
              Targets can be edited only for current or future months.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditTarget(false)}>Cancel</Button>
          <Button onClick={saveEditedTarget} variant="contained" disabled={savingEditTarget || isPastMonth}>
            {savingEditTarget ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {viewMode === "all" && (
        <div className="targets-allGrid">
          <DataGridTable columns={allGridColumns} data={allGridRows} storageKey="targets_all_grid" />
        </div>
      )}

      <Dialog open={openAddCategory} onClose={() => setOpenAddCategory(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Category Target</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ marginTop: 1 }}>
            <InputLabel id="targets-add-category-mode-label">Mode</InputLabel>
            <Select
              labelId="targets-add-category-mode-label"
              value={categoryAddMode}
              label="Mode"
              onChange={(e) => {
                const mode = e.target.value;
                setCategoryAddMode(mode);
                if (mode === "bulk") {
                  openBulkCategoryModalInternal();
                  setOpenAddCategory(false);
                }
              }}
            >
              <MenuItem value="single">Add Category Target</MenuItem>
              <MenuItem value="bulk">Bulk Category Targets</MenuItem>
            </Select>
          </FormControl>

          <div className="targets-modalFiltersRow">
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="targets-modal-person-cat-label">Person</InputLabel>
              <Select
                labelId="targets-modal-person-cat-label"
                value={modalUserId == null ? "me" : String(modalUserId)}
                label="Person"
                onChange={(e) => {
                  const v = e.target.value;
                  const next = v === "me" ? null : parsePositiveInt(v);
                  setModalUserId(next);
                }}
              >
                <MenuItem value="me">Me</MenuItem>
                {employeesForTargets.map((emp) => (
                  <MenuItem key={emp.id ?? emp.user_id} value={String(emp.user_id ?? emp.id)}>
                    {emp.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="targets-modal-year-cat-label">Year</InputLabel>
              <Select
                labelId="targets-modal-year-cat-label"
                value={modalYear}
                label="Year"
                onChange={(e) => {
                  const next = parsePositiveInt(e.target.value);
                  setModalYear(next);
                }}
              >
                {yearOptions.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="targets-modal-month-cat-label">Month</InputLabel>
              <Select
                labelId="targets-modal-month-cat-label"
                value={modalMonth}
                label="Month"
                onChange={(e) => {
                  const next = parsePositiveInt(e.target.value);
                  setModalMonth(next);
                }}
              >
                {Array.from({ length: 12 }, (_, idx) => idx + 1).map((m) => (
                  <MenuItem key={m} value={m}>
                    {toMonthLabel(currentLanguage, m)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          {categoryAddMode === "single" && (
            <>
              {loadingCategories ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <FormControl fullWidth size="small" sx={{ marginTop: 1 }}>
                    <InputLabel id="targets-add-category-label">Category</InputLabel>
                    <Select
                      labelId="targets-add-category-label"
                      value={selectedCategoryId}
                      label="Category"
                      onChange={(e) => setSelectedCategoryId(e.target.value)}
                    >
                      {enquiryCategories.map((c) => (
                        <MenuItem key={c.id} value={String(c.id)}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    size="small"
                    label="Monthly Target"
                    type="number"
                    value={newCategoryTarget}
                    onChange={(e) => setNewCategoryTarget(e.target.value)}
                    sx={{ marginTop: 2 }}
                  />
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddCategory(false)}>Cancel</Button>
          <Button onClick={saveNewCategoryTarget} variant="contained" disabled={modalIsPastMonth}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAddActivity} onClose={() => setOpenAddActivity(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Activity Target</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small" sx={{ marginTop: 1 }}>
            <InputLabel id="targets-add-activity-mode-label">Mode</InputLabel>
            <Select
              labelId="targets-add-activity-mode-label"
              value={activityAddMode}
              label="Mode"
              onChange={(e) => {
                const mode = e.target.value;
                setActivityAddMode(mode);
                if (mode === "bulk") {
                  openBulkActivityModalInternal();
                  setOpenAddActivity(false);
                } else if (mode === "attendance") {
                    // Attendance is logged for "Me" only
                    setModalUserId(null);
                  openAttendanceDialogInternal();
                  setOpenAddActivity(false);
                }
              }}
            >
              <MenuItem value="single">Add Activity Target</MenuItem>
              <MenuItem value="bulk">Bulk Activity Targets</MenuItem>
              <MenuItem value="attendance">Mark Attendance</MenuItem>
            </Select>
          </FormControl>

          <div className="targets-modalFiltersRow">
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="targets-modal-person-act-label">Person</InputLabel>
              <Select
                labelId="targets-modal-person-act-label"
                value={modalUserId == null ? "me" : String(modalUserId)}
                label="Person"
                onChange={(e) => {
                  const v = e.target.value;
                  const next = v === "me" ? null : parsePositiveInt(v);
                  setModalUserId(next);
                }}
              >
                <MenuItem value="me">Me</MenuItem>
                {employeesForTargets.map((emp) => (
                  <MenuItem key={emp.id ?? emp.user_id} value={String(emp.user_id ?? emp.id)}>
                    {emp.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel id="targets-modal-year-act-label">Year</InputLabel>
              <Select
                labelId="targets-modal-year-act-label"
                value={modalYear}
                label="Year"
                onChange={(e) => {
                  const next = parsePositiveInt(e.target.value);
                  setModalYear(next);
                }}
              >
                {yearOptions.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="targets-modal-month-act-label">Month</InputLabel>
              <Select
                labelId="targets-modal-month-act-label"
                value={modalMonth}
                label="Month"
                onChange={(e) => {
                  const next = parsePositiveInt(e.target.value);
                  setModalMonth(next);
                }}
              >
                {Array.from({ length: 12 }, (_, idx) => idx + 1).map((m) => (
                  <MenuItem key={m} value={m}>
                    {toMonthLabel(currentLanguage, m)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          {activityAddMode === "single" && (
            <>
              {loadingActivities ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <FormControl fullWidth size="small" sx={{ marginTop: 1 }}>
                    <InputLabel id="targets-add-activity-label">Activity</InputLabel>
                    <Select
                      labelId="targets-add-activity-label"
                      value={selectedActivityId}
                      label="Activity"
                      onChange={(e) => setSelectedActivityId(e.target.value)}
                      disabled={!!(newActivityName || "").trim()}
                    >
                      {activities.map((a) => (
                        <MenuItem key={a.id} value={String(a.id)}>
                          {a.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    size="small"
                    label="Monthly Target"
                    type="number"
                    value={newActivityTarget}
                    onChange={(e) => setNewActivityTarget(e.target.value)}
                    sx={{ marginTop: 2 }}
                  />

                  <Typography variant="body2" sx={{ marginTop: 2 }}>
                    Or create new activity:
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label="New Activity Name"
                    value={newActivityName}
                    onChange={(e) => setNewActivityName(e.target.value)}
                    sx={{ marginTop: 1 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Description (optional)"
                    value={newActivityDesc}
                    onChange={(e) => setNewActivityDesc(e.target.value)}
                    sx={{ marginTop: 1 }}
                  />
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddActivity(false)}>Cancel</Button>
          <Button onClick={saveNewActivityTarget} variant="contained" disabled={modalIsPastMonth}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openBulkCategoryModal} onClose={() => setOpenBulkCategoryModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>Bulk Category Targets</DialogTitle>
        <DialogContent>
          {loadingBulkCategories ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <div className="targets-modalFiltersRow">
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="targets-bulk-person-cat-label">Person</InputLabel>
                  <Select
                    labelId="targets-bulk-person-cat-label"
                    value={modalUserId == null ? "me" : String(modalUserId)}
                    label="Person"
                    onChange={(e) => {
                      const v = e.target.value;
                      const next = v === "me" ? null : parsePositiveInt(v);
                      setModalUserId(next);
                    }}
                  >
                    <MenuItem value="me">Me</MenuItem>
                    {employeesForTargets.map((emp) => (
                      <MenuItem key={emp.id ?? emp.user_id} value={String(emp.user_id ?? emp.id)}>
                        {emp.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="targets-bulk-year-cat-label">Year</InputLabel>
                  <Select
                    labelId="targets-bulk-year-cat-label"
                    value={modalYear}
                    label="Year"
                    onChange={(e) => {
                      const next = parsePositiveInt(e.target.value);
                      setModalYear(next);
                    }}
                  >
                    {yearOptions.map((y) => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="targets-bulk-month-cat-label">Month</InputLabel>
                  <Select
                    labelId="targets-bulk-month-cat-label"
                    value={modalMonth}
                    label="Month"
                    onChange={(e) => {
                      const next = parsePositiveInt(e.target.value);
                      setModalMonth(next);
                    }}
                  >
                    {Array.from({ length: 12 }, (_, idx) => idx + 1).map((m) => (
                      <MenuItem key={m} value={m}>
                        {toMonthLabel(currentLanguage, m)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <Typography variant="body2" sx={{ marginBottom: 1 }}>
                Enter amounts to add to existing category targets (sum).
              </Typography>
              <div style={{ maxHeight: 380, overflowY: "auto" }}>
                {bulkCategoryRows.map((r) => (
                  <div
                    key={r.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      marginBottom: 10,
                    }}
                  >
                    <Typography style={{ minWidth: 220, flex: 1 }} variant="body2">
                      {r.name}
                    </Typography>
                    <TextField
                      size="small"
                      type="number"
                      label="Add"
                      value={r.value}
                      onChange={(e) =>
                        setBulkCategoryRows((prev) =>
                          prev.map((x) =>
                            x.id === r.id ? { ...x, value: e.target.value } : x,
                          ),
                        )
                      }
                      style={{ width: 140 }}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBulkCategoryModal(false)}>Cancel</Button>
          <Button
            onClick={saveBulkCategoryTargets}
            variant="contained"
            disabled={savingBulkCategories || loadingBulkCategories}
          >
            {savingBulkCategories ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openBulkActivityModal} onClose={() => setOpenBulkActivityModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>Bulk Activity Targets</DialogTitle>
        <DialogContent>
          {loadingBulkActivities ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <div className="targets-modalFiltersRow">
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="targets-bulk-person-act-label">Person</InputLabel>
                  <Select
                    labelId="targets-bulk-person-act-label"
                    value={modalUserId == null ? "me" : String(modalUserId)}
                    label="Person"
                    onChange={(e) => {
                      const v = e.target.value;
                      const next = v === "me" ? null : parsePositiveInt(v);
                      setModalUserId(next);
                    }}
                  >
                    <MenuItem value="me">Me</MenuItem>
                    {employeesForTargets.map((emp) => (
                      <MenuItem key={emp.id ?? emp.user_id} value={String(emp.user_id ?? emp.id)}>
                        {emp.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="targets-bulk-year-act-label">Year</InputLabel>
                  <Select
                    labelId="targets-bulk-year-act-label"
                    value={modalYear}
                    label="Year"
                    onChange={(e) => {
                      const next = parsePositiveInt(e.target.value);
                      setModalYear(next);
                    }}
                  >
                    {yearOptions.map((y) => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="targets-bulk-month-act-label">Month</InputLabel>
                  <Select
                    labelId="targets-bulk-month-act-label"
                    value={modalMonth}
                    label="Month"
                    onChange={(e) => {
                      const next = parsePositiveInt(e.target.value);
                      setModalMonth(next);
                    }}
                  >
                    {Array.from({ length: 12 }, (_, idx) => idx + 1).map((m) => (
                      <MenuItem key={m} value={m}>
                        {toMonthLabel(currentLanguage, m)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <Typography variant="body2" sx={{ marginBottom: 1 }}>
                Enter amounts to add to existing activity targets (sum).
              </Typography>
              <div style={{ maxHeight: 380, overflowY: "auto" }}>
                {bulkActivityRows.map((r) => (
                  <div
                    key={r.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      marginBottom: 10,
                    }}
                  >
                    <Typography style={{ minWidth: 220, flex: 1 }} variant="body2">
                      {r.name}
                    </Typography>
                    <TextField
                      size="small"
                      type="number"
                      label="Add"
                      value={r.value}
                      onChange={(e) =>
                        setBulkActivityRows((prev) =>
                          prev.map((x) =>
                            x.id === r.id ? { ...x, value: e.target.value } : x,
                          ),
                        )
                      }
                      style={{ width: 140 }}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBulkActivityModal(false)}>Cancel</Button>
          <Button
            onClick={saveBulkActivityTargets}
            variant="contained"
            disabled={savingBulkActivities || loadingBulkActivities}
          >
            {savingBulkActivities ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAttendanceDialog} onClose={() => setOpenAttendanceDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark Attendance</DialogTitle>
        <DialogContent>
          {loadingAttendanceActivities ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <div className="targets-modalFiltersRow">
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="attendance-person-label">Person</InputLabel>
                  <Select
                    labelId="attendance-person-label"
                    value={modalUserId == null ? "me" : String(modalUserId)}
                    label="Person"
                    onChange={async (e) => {
                      const v = e.target.value;
                      const next = v === "me" ? null : parsePositiveInt(v);
                      setModalUserId(next);
                      await loadAttendanceAllowedActivities(next, modalYear, modalMonth);
                    }}
                  >
                    <MenuItem value="me">Me</MenuItem>
                    {employeesForTargets.map((emp) => (
                      <MenuItem key={emp.id ?? emp.user_id} value={String(emp.user_id ?? emp.id)}>
                        {emp.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="attendance-year-label">Year</InputLabel>
                  <Select
                    labelId="attendance-year-label"
                    value={modalYear}
                    label="Year"
                    onChange={async (e) => {
                      const next = parsePositiveInt(e.target.value);
                      setModalYear(next);
                      // keep date in the selected month/year
                      setAttendanceDate(`${next}-${String(modalMonth).padStart(2, "0")}-01`);
                      await loadAttendanceAllowedActivities(modalUserId, next, modalMonth);
                    }}
                  >
                    {yearOptions.map((y) => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel id="attendance-month-label">Month</InputLabel>
                  <Select
                    labelId="attendance-month-label"
                    value={modalMonth}
                    label="Month"
                    onChange={async (e) => {
                      const next = parsePositiveInt(e.target.value);
                      setModalMonth(next);
                      setAttendanceDate(`${modalYear}-${String(next).padStart(2, "0")}-01`);
                      await loadAttendanceAllowedActivities(modalUserId, modalYear, next);
                    }}
                  >
                    {Array.from({ length: 12 }, (_, idx) => idx + 1).map((m) => (
                      <MenuItem key={m} value={m}>
                        {toMonthLabel(currentLanguage, m)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              {attendanceCheckLoading ? (
                <Typography variant="body2" sx={{ marginTop: 1 }}>
                  Checking targets...
                </Typography>
              ) : null}

              <FormControl fullWidth size="small" sx={{ marginTop: 1 }}>
                <InputLabel id="attendance-activity-label">Activity</InputLabel>
                <Select
                  labelId="attendance-activity-label"
                  value={attendanceActivityId}
                  label="Activity"
                  onChange={(e) => setAttendanceActivityId(e.target.value)}
                >
                  {attendanceActivities.map((a) => {
                    const disabled = attendanceCheckLoaded && !attendanceAllowedActivityIds.has(a.id);
                    return (
                      <MenuItem key={a.id} value={String(a.id)} disabled={disabled}>
                        {a.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              {attendanceCheckLoaded && attendanceActivityId && !attendanceAllowedActivityIds.has(parsePositiveInt(attendanceActivityId)) ? (
                <Typography color="error" variant="body2" sx={{ marginTop: 1 }}>
                  You cannot add attendance because selected person does not have this activity target for this month.
                </Typography>
              ) : null}
              <TextField
                fullWidth
                size="small"
                label="Date"
                type="date"
                value={attendanceDate}
                onChange={(e) => setAttendanceDate(e.target.value)}
                sx={{ marginTop: 2 }}
              />
              <FormControlLabel
                sx={{ marginTop: 2 }}
                control={
                  <Checkbox
                    checked={attendanceAttended}
                    onChange={() => {}}
                    disabled
                  />
                }
                label="Attended"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAttendanceDialog(false)}>Cancel</Button>
          <Button onClick={saveAttendance} variant="contained" disabled={savingAttendance || attendanceCheckLoading}>
            {savingAttendance ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openLogActivity} onClose={() => setOpenLogActivity(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Log Activity</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            {activityForLog?.name}
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Date"
            type="date"
            value={logDate}
            onChange={(e) => setLogDate(e.target.value)}
          />
          <FormControlLabel
            sx={{ marginTop: 2 }}
            control={
              <Checkbox
                checked={logAttended}
                onChange={(e) => setLogAttended(e.target.checked)}
              />
            }
            label="Attended"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLogActivity(false)}>Cancel</Button>
          <Button onClick={saveLogActivity} variant="contained" disabled={savingLog}>
            {savingLog ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

